import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { 
  DollarSign, 
  Target, 
  Users, 
  FileText,
  Eye,
  Briefcase,
  X
} from 'lucide-react';
import Button from '../common/Button';


const SubscriptionPlanModal = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  isLoading = false,
  title = "Create Subscription Plan",
  submitButtonText = "Create Plan"
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    target_role: 'professional',
    job_application_limit: '',
    job_posting_limit: '',
    profile_view_limit: '',
    is_active: true,
    features: ""
  });

  const [errors, setErrors] = useState({});

  // Initialize form with initialData when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        currency: initialData.currency || 'USD',
        target_role: initialData.target_role || 'professional',
        job_application_limit: initialData.job_application_limit || '',
        job_posting_limit: initialData.job_posting_limit || '',
        profile_view_limit: initialData.profile_view_limit || '',
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
        features: initialData.features || ''
      });
    } else {
      // Reset form when creating new
      setFormData({
        name: '',
        description: '',
        price: '',
        currency: 'USD',
        target_role: 'professional',
        job_application_limit: '',
        job_posting_limit: '',
        profile_view_limit: '',
        is_active: true,
        features: ''
      });
    }
    setErrors({});
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    }

    // if (!formData.description.trim()) {
    //   newErrors.description = 'Description is required';
    // }

    if (!formData.price || parseFloat(formData.price) < 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    }

    if (!formData.target_role) {
      newErrors.target_role = 'Target role is required';
    }

    if (formData.job_application_limit !== '' && parseInt(formData.job_application_limit) < 0) {
      newErrors.job_application_limit = 'Application limit must be non-negative';
    }

    if (formData.job_posting_limit !== '' && parseInt(formData.job_posting_limit) < 0) {
      newErrors.job_posting_limit = 'Job posting limit must be non-negative';
    }

    if (formData.profile_view_limit !== '' && parseInt(formData.profile_view_limit) < 0) {
      newErrors.profile_view_limit = 'Profile view limit must be non-negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convert numeric fields
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        job_application_limit: formData.job_application_limit ? parseInt(formData.job_application_limit) : 0,
        job_posting_limit: formData.job_posting_limit ? parseInt(formData.job_posting_limit) : 0,
        profile_view_limit: formData.profile_view_limit ? parseInt(formData.profile_view_limit) : 0,
      };
      
      onSubmit(submitData);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit} className='sentry-mask'>
        <div>

        <DialogTitle className="border-b">
          <div className="flex items-center space-x-2">
            <Briefcase className="w-6 h-6 text-blue-600" />
            <Typography variant="h6" className="text-secondary-900">
              {title}
            </Typography>
          </div>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'gray',
          }}
        >
          <X />
        </IconButton>
        </div>

        <DialogContent className="pt-6">
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid size={{xs: 12}}>
              <Typography variant="subtitle1" className="font-semibold mb-3 text-secondary-900">
                Basic Information
              </Typography>
            </Grid>

            <Grid size={{xs: 12, md:6}}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
                placeholder="e.g., Professional Premium"
              />
            </Grid>

            <Grid size={{xs: 12, md:6}}>
              <FormControl fullWidth required error={!!errors.target_role}>
                <InputLabel>Target Role</InputLabel>
                <Select
                  value={formData.target_role}
                  label="Target Role"
                  onChange={handleChange('target_role')}
                  startAdornment={<Target className="w-4 h-4 text-gray-400 mr-2" />}
                >
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="recruiter">Recruiter</MenuItem>
                </Select>
                {errors.target_role && (
                  <Typography variant="caption" color="error">
                    {errors.target_role}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                error={!!errors.description}
                helperText={errors.description}
                // required
                multiline
                rows={3}
                placeholder="Describe the benefits of this plan..."
              />
            </Grid>
            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                label="Features"
                value={formData.features}
                onChange={handleChange('features')}
                error={!!errors.features}
                helperText={errors.features}
                // required
                multiline
                rows={3}
                placeholder="Describe the features of this plan..."
              />
            </Grid>

            {/* Pricing */}
            <Grid size={{xs: 12}}>
              <Typography variant="subtitle1" className="font-semibold mb-3 text-secondary-900">
                Pricing
              </Typography>
            </Grid>

            <Grid size={{xs: 12, md:6}}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={formData.price}
                onChange={handleChange('price')}
                error={!!errors.price}
                helperText={errors.price}
                required
                slotProps={{input: {
                  startAdornment: (
                    <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                  ),
                }}}
                placeholder="0.00"
              />
            </Grid>

            <Grid size={{xs: 12, md:6}}>
              <FormControl fullWidth required error={!!errors.currency}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  label="Currency"
                  onChange={handleChange('currency')}
                >
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="KES">KES (KSh)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                </Select>
                {errors.currency && (
                  <Typography variant="caption" color="error">
                    {errors.currency}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Usage Limits */}
            <Grid size={{xs: 12}}>
              <Typography variant="subtitle1" className="font-semibold mb-3 text-secondary-900">
                Usage Limits
              </Typography>
              <Typography variant="body2" className="text-gray-600 mb-4">
                Set limits for key features. Use 0 for unlimited or leave empty for no access.
              </Typography>
            </Grid>

            <Grid size={{xs: 12, md:4}}>
              <TextField
                fullWidth
                label="Job Applications"
                type="number"
                value={formData.job_application_limit}
                onChange={handleChange('job_application_limit')}
                error={!!errors.job_application_limit}
                helperText={errors.job_application_limit}
                slotProps={{input:{
                  startAdornment: (
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                  ),
                }}}
                placeholder="-1 for unlimited"
              />
            </Grid>

            <Grid size={{xs: 12, md:4}}>
              <TextField
                fullWidth
                label="Job Postings"
                type="number"
                value={formData.job_posting_limit}
                onChange={handleChange('job_posting_limit')}
                error={!!errors.job_posting_limit}
                helperText={errors.job_posting_limit}
                slotProps={{input:{
                  startAdornment: (
                    <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
                  ),
                }}}
                placeholder="-1 for unlimited"
              />
            </Grid>

            <Grid size={{xs: 12, md:4}}>
              <TextField
                fullWidth
                label="Profile Views"
                type="number"
                value={formData.profile_view_limit}
                onChange={handleChange('profile_view_limit')}
                error={!!errors.profile_view_limit}
                helperText={errors.profile_view_limit}
                slotProps={{input:{
                  startAdornment: (
                    <Eye className="w-4 h-4 text-gray-400 mr-2" />
                  ),
                }}}
                placeholder="-1 for unlimited"
              />
            </Grid>

            {/* Status */}
            <Grid size={{xs: 12}}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange('is_active')}
                    color="primary"
                  />
                }
                label={
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Active Plan</span>
                    <Typography variant="body2" className="text-gray-600">
                      {formData.is_active ? 'Plan is available for purchase' : 'Plan is hidden from users'}
                    </Typography>
                  </div>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className="border-t p-4">
          <div className="flex space-x-3 w-full justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
            >
              {submitButtonText}
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubscriptionPlanModal;